import type { ExecuteResponse } from '../models/execute.types'
import type { InsightAuthoringHistoryParams } from '../models/insightApi.types'
import type {
  InsightAuthoringHistoryEntry,
  InsightDraftRequest,
  InsightDraftResponse,
} from '../models/insightAuthoring.types'
import type { InsightConfig } from '../models/insightConfig.types'
import { normalizePaginatedResponse } from '../service/normalizeListResponse'

import { cleanQueryParams } from './cleanQueryParams'

import { INSIGHT_AUTHORING_MODEL } from '@/shared/config'
import { appHttp, unwrap, type Paginated } from '@/shared/http'

export interface InsightAuthoringDryRunInput {
  accountId: number
  workspaceId: number
  scheduleId?: number | null
  force?: boolean
}

function requestConfig(params: object, signal?: AbortSignal) {
  return {
    params: cleanQueryParams(params),
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true },
    ...(signal ? { signal } : {}),
  }
}

/** Blocking request/response AI authoring transport. */
export class InsightAuthoringApi {
  private readonly base = '/admin-panel/insights/configs/'

  constructor(private readonly http = appHttp) {}

  async draft(input: InsightDraftRequest, signal?: AbortSignal): Promise<InsightDraftResponse> {
    const response = await this.http.post(
      `${this.base}draft/`,
      {
        session_id: input.sessionId ?? null,
        user_message: input.userMessage,
        seed: input.seed ?? null,
        previous_draft: input.previousDraft ?? null,
        model: input.model ?? INSIGHT_AUTHORING_MODEL,
      },
      requestConfig({}, signal),
    )
    return unwrap<InsightDraftResponse>(response)
  }

  async save(sessionId: string, turnId: number, signal?: AbortSignal): Promise<InsightConfig> {
    const response = await this.http.post(
      `${this.base}draft/${encodeURIComponent(sessionId)}/save/`,
      { turn_id: turnId },
      requestConfig({}, signal),
    )
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async dryRun(
    configId: number,
    input: InsightAuthoringDryRunInput,
    signal?: AbortSignal,
  ): Promise<ExecuteResponse> {
    const response = await this.http.post(
      `${this.base}${configId}/dry-run/`,
      {
        account_id: input.accountId,
        workspace_id: input.workspaceId,
        schedule_id: input.scheduleId ?? null,
        force: input.force ?? true,
      },
      requestConfig({}, signal),
    )
    return unwrap<ExecuteResponse>(response)
  }

  async activate(
    configId: number,
    targetStatus: 'active' | 'paused' = 'active',
    signal?: AbortSignal,
  ): Promise<InsightConfig> {
    const response = await this.http.post(
      `${this.base}${configId}/activate/`,
      { target_status: targetStatus },
      requestConfig({}, signal),
    )
    return unwrap<{ data: InsightConfig }>(response).data
  }

  async history(
    params: InsightAuthoringHistoryParams = {},
    signal?: AbortSignal,
  ): Promise<Paginated<InsightAuthoringHistoryEntry>> {
    const response = await this.http.get(
      `${this.base}authoring/history/`,
      requestConfig(params, signal),
    )
    return normalizePaginatedResponse<InsightAuthoringHistoryEntry>(response.data, params)
  }
}

export const insightAuthoringApi = new InsightAuthoringApi()
